import React, { useMemo } from 'react';
import { AnimalAvatar, Icon } from '../../components';
import './Radar.css';

// FNV-1a 32-bit. Same algorithm used in BubblesBg / AnimalAvatar; cheap and
// stable. Returns an unsigned int.
const fnv1a = (str) => {
  let h = 2166136261 >>> 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Map a peer to a stable polar position on the radar:
//  - angle: deterministic from userId
//  - radius (fraction of half-size, 0.15-0.85):
//      bars 4 → 0.30 (close to center)
//      bars 3 → 0.45
//      bars 2 → 0.60
//      bars 1 → 0.75
//      null  → 0.85 (outer ring — "unmeasured")
//
// Output coordinates are expressed as CSS percentages, suitable for direct
// assignment to `top`/`left` on a `position:absolute` child of `.radar`.
const polarToCss = (userId, signalBars) => {
  const angleDeg = fnv1a(userId) % 360;
  const radius =
    signalBars == null ? 0.85 : ((5 - signalBars) / 4) * 0.7 + 0.15;
  const rad = (angleDeg * Math.PI) / 180;
  // CSS positive Y is down — sin gives vertical offset, cos horizontal.
  const top = 50 + radius * Math.sin(rad) * 50;
  const left = 50 + radius * Math.cos(rad) * 50;
  return { top: `${top}%`, left: `${left}%` };
};

/**
 * F10.1 — animated radar with one blip per nearby peer.
 *
 * Mirrors the markup from `design/sharing-screens.jsx` lines 132-147:
 * three concentric rings + a conic-gradient sweep + a center device icon +
 * one `<span class="blip">` per peer carrying an AnimalAvatar, a `.ping`
 * pulse, and a label.
 *
 * Props:
 *  - `peers`        — `Peer[]` from `useNearbyPeers`. Each peer needs at
 *                     minimum `userId`, `handle`, `avatarSeed`, `signalBars`.
 *  - `selfDevice`   — `'phone' | 'monitor'`, drives the center icon.
 *  - `onSelectPeer` — `(userId) => void`, fires when a blip is tapped.
 *
 * Animations (`.sweep` rotation + `.ping` pulse) are disabled under
 * `prefers-reduced-motion: reduce` via a CSS-only media rule in Radar.css.
 */
const Radar = ({ peers = [], selfDevice = 'phone', onSelectPeer }) => {
  // Memoise per-peer geometry so re-renders don't move blips around. The
  // identity of a peer (userId + signalBars) is the only input that matters;
  // a parent rerender with the same peers array should produce identical
  // CSS offsets.
  const placed = useMemo(
    () =>
      peers.map((peer) => ({
        ...peer,
        pos: polarToCss(peer.userId, peer.signalBars),
      })),
    [peers],
  );

  const handleClick = (id) => () => {
    if (typeof onSelectPeer === 'function') onSelectPeer(id);
  };
  const handleKey = (id) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (typeof onSelectPeer === 'function') onSelectPeer(id);
    }
  };

  const centerIcon = selfDevice === 'monitor' ? 'monitor' : 'phone-device';

  return (
    <div className="radar" role="presentation">
      <span className="ring" aria-hidden="true" />
      <span className="ring r2" aria-hidden="true" />
      <span className="ring r3" aria-hidden="true" />
      <span className="sweep" aria-hidden="true" />
      <span className="center" aria-hidden="true">
        <Icon name={centerIcon} size={22} stroke="#fff" />
      </span>
      {placed.map((peer) => (
        <span
          className="blip"
          key={peer.userId}
          style={peer.pos}
          role={onSelectPeer ? 'button' : undefined}
          tabIndex={onSelectPeer ? 0 : undefined}
          aria-label={onSelectPeer ? `Beam to ${peer.handle}` : undefined}
          onClick={onSelectPeer ? handleClick(peer.userId) : undefined}
          onKeyDown={onSelectPeer ? handleKey(peer.userId) : undefined}
          data-user-id={peer.userId}
        >
          <span className="ping" aria-hidden="true" />
          <AnimalAvatar
            seed={peer.avatarSeed || peer.userId}
            size={34}
            ring="rgba(63,220,138,.6)"
          />
          <span className="label">{peer.handle}</span>
        </span>
      ))}
    </div>
  );
};

export default Radar;
// Exported for tests that want to verify deterministic placement without
// having to render the whole radar.
export const __polarToCss = polarToCss;
